import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  InputBase,
  List,
  ListItem,
  IconButton,
  Pagination,
  useTheme,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Modal from "@mui/material/Modal";
import Navbar from "../components/Navigation";
import { AuthTokenContext } from "../contexts/AuthTokenContext";
import { TenantContext } from "../contexts/TenantContext";
import API_ENDPOINTS from "../utilities/APIRoutes";
import SnackbarAlert from "../components/SnackbarAlert";
import Footer from "../components/Footer";
import Fuse from "fuse.js";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    padding: theme.spacing(0.2),
  },
  description: {
    alignSelf: "flex-start",
    color: "#ffffff",
    fontSize: "1.2rem",
    fontWeight: "bold",
    fontFamily: "Raleway, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem", // font size for smaller screens
    },
  },
  centerContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
    fontFamily: "Raleway, sans-serif",
    padding: theme.spacing(2),
    marginTop: theme.spacing(14),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(7),
      marginTop: theme.spacing(10),
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6.5),
      marginTop: theme.spacing(6),
    },
  },
  userList: {
    marginTop: "1em",
  },
  listItem: {
    backgroundColor: "#303030",
    marginBottom: "0.5em",
    borderRadius: 8,
    height: theme.spacing(6),
  },
  listItemText: {
    marginLeft: theme.spacing(0.4),
    color: "white",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
      fontWeight: "bold",
    },
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    width: 300,
    height: 35,
    justifyContent: "space-between",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    padding: "2px 12px",
    [theme.breakpoints.down("sm")]: {
      width: "50%",
    },
  },
  iconBox: {
    display: "flex",
    width: "auto",
    gap: theme.spacing(1),
    justifyContent: "space-around",
  },
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 400,
    backgroundColor: "#202020",
    borderRadius: "9px",
    padding: theme.spacing(5),
  },
  footerContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    backgroundColor: "#FFC629",
    padding: theme.spacing(2),
  },
  footer: {
    color: "#747171",
    fontSize: "0.8rem",
  },
}));

function TenantManagement() {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const { authInfo } = useContext(AuthTokenContext);
  const [tenants, setTenants] = useState([]);
  const { setTenantId } = useContext(TenantContext);
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };
  const handleOpen = (tenantId) => {
    setTenantToDelete(tenantId);
    setOpen(true);
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const UserToken = localStorage.getItem("userToken");
  const AuthToken = localStorage.getItem("authToken");
  useEffect(() => {
    const fetchTenants = async () => {
      if (!UserToken || !AuthToken) {
        console.error("No user token or auth token found in local storage");
        return;
      }
      try {
        const response = await fetch(API_ENDPOINTS.ListTenant, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "User-Token": UserToken,
            Authorization: AuthToken,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch tenants");
        }

        const data = await response.json();

        setTenants(data.message);
      } catch (error) {
        console.error("Error fetching tenants:", error);
      }
    };

    // Check if we navigated here with a success message
    if (location.state?.success) {
      setSnackbarMessage(location.state.message);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }

    fetchTenants();
  }, [location.state]);

  const deleteTenant = async (tenantId) => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.DeleteTenant, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "User-Token": UserToken,
          Authorization: AuthToken,
        },
        body: JSON.stringify({ tenantID: tenantId }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      if (
        result.message === "Tenant successfully deleted from DynamoDB" ||
        result.errorCode === 200
      ) {
        setSnackbarMessage("Tenant deleted successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTenants((prevTenants) =>
          prevTenants.filter((tenant) => tenant.id !== tenantId)
        );
        handleClose();
      } else {
        setSnackbarMessage("Something went wrong deleting this tenant!");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        console.error("Failed to delete tenant:", result.message);
      }
    } catch (error) {
      console.error("Failed to delete tenant:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
  };

  const handleEditClick = (tenantID) => {
    localStorage.setItem("tenantId", tenantID);
    navigate("edit-tenant");
  };

  const fuse = new Fuse(tenants, {
    keys: ["tenantName"],
    threshold: 0.4,
  });

  const filteredTenants = searchQuery
    ? fuse.search(searchQuery).map((result) => result.item)
    : tenants;

  const tenantsToDisplay = filteredTenants.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <Box className={classes.root}>
      <Navbar />
      <Box className={classes.centerContent}>
        <Paper
          square={false}
          elevation={0}
          variant="elevation"
          style={{
            backgroundColor: "black",
            width: "100%",
            maxWidth: 1300,
            height: "max-content",
            padding: 35,
            borderRadius: 14,
          }}
        >
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              // alignItems: "center",
              // marginRight: theme.spacing(3.5),
              // flexWrap: "wrap",
              // gap: theme.spacing(2),
            }}
          >
            <Typography
              className={classes.description}
              style={{
                alignSelf: "flex-start",
                color: "#ffffff",
                fontSize: "1.2rem",
                fontWeight: "bold",
                fontFamily: "Raleway, sans-serif",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "1rem", // font size for smaller screens
                },
              }}
            >
              Tenant management
            </Typography>
            <Tooltip title="Add Tenant">
              <IconButton onClick={() => navigate("add-tenant")}>
                <AddBoxRoundedIcon
                  fontSize="large"
                  style={{ color: "#FFC629" }}
                />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <Paper
              component="form"
              className={classes.searchBox}
              style={{ borderRadius: "8px" }}
              onSubmit={handleSearchSubmit}
            >
              <InputBase
                className={classes.searchInput}
                placeholder="Search Tenants"
                inputProps={{ "aria-label": "search user" }}
                style={{ fontFamily: "Poppins, sans-serif", fontSize: "1rem" }}
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <Tooltip title="Search">
                <IconButton type="submit" sx={{ p: "10px" }} aria-label="search">
                  <SearchRoundedIcon />
                </IconButton>
              </Tooltip>
            </Paper>
            <Box className={classes.userList}>
              <List>
                {filteredTenants.length > 0 ? (
                  tenantsToDisplay.map((tenant) => (
                    <ListItem
                      key={tenant.id}
                      className={classes.listItem}
                      sx={{
                        justifyContent: "space-between",
                        padding: theme.spacing(1.4),
                      }}
                    >
                      <ListItemText
                        className={classes.listItemText}
                        primary={tenant.tenantName}
                        primaryTypographyProps={{
                          style: {
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "0.9rem",
                            [theme.breakpoints.down("sm")]: {
                              fontSize: "0.6rem",
                            },
                          },
                        }}
                      />

                      <Box className={classes.iconBox}>
                        <Tooltip title="Edit tenant">
                          <IconButton
                            type="button"
                            aria-label="edit"
                            style={{
                              backgroundColor: "#FFC629",
                              borderRadius: 4,
                              width: 25,
                              height: 25,
                            }}
                            onClick={() => handleEditClick(tenant.id)}
                          >
                            <EditIcon
                              sx={{ color: "black", width: 20, height: 20 }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete tenant">
                          <IconButton
                            aria-label="delete"
                            style={{
                              backgroundColor: "#FFC629",
                              borderRadius: 4,
                              width: 25,
                              height: 25,
                            }}
                            onClick={() => handleOpen(tenant.id)}
                          >
                            <DeleteOutlineOutlinedIcon
                              sx={{ color: "black", width: 20, height: 20 }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Modal
                          open={open}
                          onClose={handleClose}
                          aria-labelledby="modal-modal-title"
                          aria-describedby="modal-modal-description"
                        >
                          <Box className={classes.modal}>
                            <Typography
                              id="modal-modal-title"
                              variant="h6"
                              component="h2"
                              style={{
                                textAlign: "center",
                                fontFamily: "Raleway",
                                fontWeight: "bolder",
                                color: "white",
                                fontSize: "1.5rem",
                              }}
                            >
                              Are you sure you want to delete <span style={{ color: "#FFC629" }}>'{tenant.tenantName}'</span> ?
                            </Typography>
                            <Box
                              style={{
                                display: "flex",
                                justifyContent: "space-evenly",
                                marginTop: theme.spacing(3),
                              }}
                            >
                              <Button
                                disabled={loading}
                                onClick={() => deleteTenant(tenantToDelete)}
                                style={{
                                  backgroundColor: "#FFC629",
                                  color: "#000000",
                                  borderRadius: "10px",
                                  padding: theme.spacing(1, 3),
                                  fontSize: "1rem",
                                  fontWeight: "bold",
                                  textTransform: "none",
                                  fontFamily: "Raleway, sans-serif",
                                  "&:hover": {
                                    backgroundColor: "#FFC629",
                                    color: "#000000",
                                  },
                                  [theme.breakpoints.down("sm")]: {
                                    fontSize: "0.9rem", // font size for smaller screens
                                  },
                                }}
                              >
                                {loading ? "Deleting . . ." : "Delete"}
                              </Button>
                              <Button
                                onClick={() =>
                                  (window.location.href = "/tenant-management")
                                }
                                style={{ color: "white" }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Box>
                        </Modal>
                      </Box>
                    </ListItem>
                  ))
                ) : (
                  <Typography
                    style={{
                      color: "#999",
                      textAlign: "center",
                      fontFamily: "Raleway, sans-serif",
                      padding: theme.spacing(2),
                    }}
                  >
                    {searchQuery
                      ? "No tenants match your search."
                      : "No tenants available. Add one to see it displayed here."}
                  </Typography>
                )}
              </List>
              {filteredTenants.length > pageSize && (
                <Pagination
                  sx={{
                    display: "inline-flex",
                    backgroundColor: "#FFC629",
                    borderRadius: 1,
                    width: "fit-content",
                    marginBottom: theme.spacing(3),
                  }}
                  size="small"
                  shape="rounded"
                  count={Math.ceil(filteredTenants.length / pageSize)}
                  page={currentPage}
                  onChange={handlePageChange}
                />
              )}
            </Box>
          </Box>
        </Paper>
        <SnackbarAlert
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={handleCloseSnackbar}
        />
      </Box>
      <Footer />
    </Box>
  );
}

export default TenantManagement;
